import { apiFetch } from './api';
import { useAuthStore } from '../store/useAuthStore';

// Bank accounts API client. Mirrors backend/src/routes/bankAccounts.ts.
// GET is available to any authenticated user; writes require
// settings.canManageCompanies.

export interface BankAccount {
  id: string;
  iban: string;
  bankName: string;
  label: string | null;
  currency: string;
  isActive: boolean;
  companyId: string | null;
  company: { id: string; name: string } | null;
  /** Manual balance override for the cashflow card (null = none set). */
  manualBalance: number | null;
  manualBalanceAt: string | null;
  statementsCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface BankAccountInput {
  iban: string;
  bankName: string;
  label?: string | null;
  currency?: 'RON' | 'EUR' | 'USD';
  companyId?: string | null;
}

export interface BankAccountUpdateInput {
  iban?: string;
  bankName?: string;
  label?: string | null;
  currency?: 'RON' | 'EUR' | 'USD';
  isActive?: boolean;
  /** Number stamps manualBalanceAt=now on the backend; null clears the override. */
  manualBalance?: number | null;
}

export interface DeleteBankAccountResult {
  deleted: boolean;
  deactivated: boolean;
  reason?: string;
}

interface Envelope<T> {
  success: true;
  data: T;
}

function authHeaders(): Record<string, string> {
  const token = useAuthStore.getState().accessToken;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export async function listBankAccounts(companyId?: string): Promise<BankAccount[]> {
  const qs = companyId ? `?companyId=${encodeURIComponent(companyId)}` : '';
  const res = await apiFetch<Envelope<{ accounts: BankAccount[] }>>(`/api/bank-accounts${qs}`, {
    headers: authHeaders(),
  });
  return res.data.accounts;
}

export async function createBankAccount(input: BankAccountInput): Promise<BankAccount> {
  const res = await apiFetch<Envelope<{ account: BankAccount }>>('/api/bank-accounts', {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(input),
  });
  return res.data.account;
}

export async function updateBankAccount(
  id: string,
  input: BankAccountUpdateInput,
): Promise<BankAccount> {
  const res = await apiFetch<Envelope<{ account: BankAccount }>>(`/api/bank-accounts/${id}`, {
    method: 'PATCH',
    headers: authHeaders(),
    body: JSON.stringify(input),
  });
  return res.data.account;
}

export async function deleteBankAccount(id: string): Promise<DeleteBankAccountResult> {
  const res = await apiFetch<Envelope<DeleteBankAccountResult>>(`/api/bank-accounts/${id}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  return res.data;
}
